/**
 * Which category a catalog component belongs to, for the admin editor's
 * grouping (SP3 spec §2.2).
 *
 * The list is DATA, in vendorRegistry — not conventions inferred here. This
 * module only applies it. Matching is ordered and first-match-wins.
 *
 * A component matching nothing is `uncategorised`, and the views render that
 * group like any other. That visibility is what makes prefix matching
 * acceptable: a new SKU naming pattern shows up as an unclaimed row rather
 * than being silently absorbed by a neighbouring bucket.
 */
import { isNodeSku } from '../../pricing/seedBuilder';
import { isCostKind } from '../../catalog/costDraft';
import { productSkusOf } from '../../catalog/catalogSkus';

export const UNCATEGORISED = 'uncategorised';
export const UNCATEGORISED_LABEL = 'Uncategorised';

const BASE_PREFIX = 'CMP-BASE-';

/** The product SKU a component describes: `CMP-BASE-<sku>` carries one, every
 *  other component stands for itself. Nodes and network cards BOTH begin
 *  `CMP-BASE-VCH-5000-`, so the product SKU — not the component SKU — is what
 *  `isNodeSku` must be asked about. */
export function productSkuOf(componentSku) {
  return componentSku.startsWith(BASE_PREFIX)
    ? componentSku.slice(BASE_PREFIX.length)
    : componentSku;
}

function matches(category, componentSku, kind, productSku, notDecomposed) {
  if (category.notDecomposed) {
    return category.kinds?.includes(kind) && notDecomposed.has(productSku);
  }
  if (category.kinds?.includes(kind)) return true;
  if (category.prefixes?.some((p) => componentSku.startsWith(p))) return true;
  if (category.productPrefixes?.some((p) => productSku.startsWith(p))) return true;
  if (category.node && isNodeSku(productSku)) return true;
  return false;
}

export function categoryOf(componentSku, kind, categories, notDecomposed = new Set()) {
  const productSku = productSkuOf(componentSku);
  for (const category of categories ?? []) {
    if (matches(category, componentSku, kind, productSku, notDecomposed)) return category.id;
  }
  return UNCATEGORISED;
}

/** Components that sit in every composition of their class and therefore say
 *  nothing about a product's own cost. `CMP-LABOR-NODE` is in every node's
 *  BASELINE composition (seedBuilder.ts), so a composition of "my residual plus
 *  labor" is not a decomposition — it is an undecomposed node. */
const OVERHEAD_SKUS = new Set(['CMP-LABOR-NODE']);

/**
 * Product SKUs whose composition contains no real cost basis — nothing but
 * their own `CMP-BASE-<sku>` residual, plus shared overhead.
 *
 * COMPUTED, never a hand-kept list: a SKU that arrives without a BOM classifies
 * itself, and one that gets decomposed leaves on its own. The highest version
 * wins, matching `snapshotFromSeed`'s version precedence — unlike it, this is
 * QUARTER-AGNOSTIC. `snapshotFromSeed` (seedBuilder.ts:313-318) picks the
 * highest version whose components are ALL costed THAT QUARTER, falling back
 * to a lower version (typically v1) otherwise; this helper ignores costing
 * entirely and just looks at composition shape. Visible consequence: in a
 * quarter where the 26T FRU doesn't resolve, the pricing engine falls back to
 * pricing the v1 lump, but the quarter-scoped ImpactView still shows the
 * product's residual under "Residual remainders" rather than the lump
 * category, because this quarter-agnostic check still sees the v2
 * decomposition and reports the product as decomposed.
 */
export function notDecomposedProductSkus(seed) {
  const best = new Map();
  for (const c of seed?.compositions ?? []) {
    const prev = best.get(c.productSku);
    if (!prev || (c.version ?? 0) > (prev.version ?? 0)) best.set(c.productSku, c);
  }

  const out = new Set();
  for (const [productSku, comp] of best) {
    const basis = (comp.components ?? []).filter(
      (x) => x.componentSku !== `CMP-BASE-${productSku}` && !OVERHEAD_SKUS.has(x.componentSku),
    );
    if (basis.length === 0) out.add(productSku);
  }
  return out;
}

/** How many product SKUs this component is composed into — the leverage number.
 *  1 for a `CMP-BASE-<sku>` by construction; the real ripple for a shared part;
 *  0 for an orphan, which the views flag.
 *
 *  Deduped by productSku via `productSkusOf` (catalogSkus.ts), the same
 *  dedupe the live impact panel's "X of Y SKUs moved" count uses — NOT a raw
 *  `.length` over the matching compositions. `mergeSeed` keeps every
 *  composition VERSION side by side rather than dropping superseded ones
 *  (seedBuilder.ts:286), so a component present in both a productSku's v1
 *  baseline composition and its v2 decomposition matches twice for ONE
 *  product it actually ships in — `.length` would report 2 there when the
 *  true leverage is 1. */
export function usedByCount(seed, componentSku) {
  const matching = (seed?.compositions ?? []).filter(
    (c) => (c.components ?? []).some((x) => x.componentSku === componentSku),
  );
  return productSkusOf({ compositions: matching }).length;
}

/** Category id -> { label, skus[] }, in the registry's order, with
 *  Uncategorised last and ALWAYS present when it has members. */
export function groupByCategory(components, categories, seed) {
  const notDecomposed = notDecomposedProductSkus(seed);
  const kindOf = new Map();
  for (const c of components ?? []) {
    if (isCostKind(c.kind)) kindOf.set(c.componentSku, c.kind);
  }

  const buckets = new Map((categories ?? []).map((c) => [c.id, { id: c.id, label: c.label, skus: [] }]));
  buckets.set(UNCATEGORISED, { id: UNCATEGORISED, label: UNCATEGORISED_LABEL, skus: [] });

  for (const sku of [...kindOf.keys()].sort()) {
    buckets.get(categoryOf(sku, kindOf.get(sku), categories, notDecomposed)).skus.push(sku);
  }
  return [...buckets.values()].filter((b) => b.skus.length > 0);
}
